import { useQuery } from "@tanstack/react-query"

import { STATES_IN_SESSION_NAME, STATES_OUTTA_SESSION_NAME } from "shared/imports/sharedConstants"

import { CustomRegion } from "@/types/djangio"
import { RecentRegion } from "@/components/TopNavigation/components/RegionSelector/types"
import {
    CUSTOM_REGIONS_QUERY_KEY,
    REGION_ABBREV_VALUE_MAPPING,
    RECENT_REGIONS_QUERY_KEY,
} from "@/components/TopNavigation/components/RegionSelector/constants"

const getCustomRegionDescription = (region: CustomRegion) =>
    region.regions
        .map((abbrev) => DjangIO.app.models.SupportedRegion.by_value(REGION_ABBREV_VALUE_MAPPING[abbrev]).region_name)
        .join(", ")

const sortByRegionName = (name) => (a, b) => {
    if (a.name === name) {
        return -1
    }
    return b.name === name ? 1 : 0
}

// based on app/static/frontend/subfeatures/RegionSelectionPage/selectors.js > selectCustomRegionsToDisplay
export const transformCustomRegions = (customRegions: CustomRegion[]) =>
    customRegions
        .map((customRegion) => ({
            label: customRegion.name,
            value: `custom-${customRegion.name}`,
            description: getCustomRegionDescription(customRegion),
            disabledRegion: false,
            ...customRegion,
        }))
        // Sort STATES_IN_SESSION_NAME to be in the front of the list, followed by outta session
        .sort(sortByRegionName(STATES_OUTTA_SESSION_NAME))
        .sort(sortByRegionName(STATES_IN_SESSION_NAME))

const fetchCustomRegions = async (): Promise<CustomRegion[]> => {
    const response = await DjangIO.app.userdata.models.CustomRegionSelection.objects
        .filter({
            limit: 0,
        })
        .GET()

    return response.objects
}

export const useCustomRegions = () =>
    useQuery({
        queryKey: [CUSTOM_REGIONS_QUERY_KEY],
        queryFn: fetchCustomRegions,
        select: transformCustomRegions,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    })

const fetchRecentRegions = async (): Promise<RecentRegion[]> => {
    const response = await DjangIO.app.useractions.models.UserAction.objects.use_action("recent_regions").GET()

    return response.objects
}

export const useRecentRegions = () =>
    useQuery({
        queryKey: [RECENT_REGIONS_QUERY_KEY],
        queryFn: fetchRecentRegions,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    })
